import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ContactType } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { ImportContactsComponent } from '@app/preferences/components/contacts/import-contacts/import-contacts.component';
import { CallingSettings } from '@app/preferences/models/settings.models';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { ContactTypeLabelPipe } from '@app/shared/pipes/contact-filter-labels.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { saveAs } from 'file-saver-es';
import { firstValueFrom } from 'rxjs';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatSnackBarModule,
    MatDialogModule,
    MatDividerModule,
    MatSelectModule,
    MatIconModule,
    ContactTypeLabelPipe,
    TranslateModule,
    BrandingColorDirective,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatButtonModule,
  ],
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
})
export class ContactsComponent implements OnInit {
  protected selectedContactOption: ContactType = ContactType.Personal;
  protected readonly ContactType = ContactType;
  public contactMatching: FormControl;
  protected contactMatchingLoading = false;
  private callingConfig: CallingSettings;
  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private contactService: ContactService,
    protected appConfigService: AppConfigService,
    private snackBar: SnackbarService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private configService: AppConfigService,
    private translate: AppTranslateService,
    protected brandingService: BrandingService
  ) {
    this.contactMatching = this.fb.control(true);
  }

  ngOnInit(): void {
    this.configService.data$.pipe(untilDestroyed(this)).subscribe((config) => {
      if (config && config.settings?.preferences) {
        this.callingConfig = config.settings.preferences.calling || {};
        this.contactMatching.setValue(!config.settings.preferences.calling?.disableContactMatching);
      }
    });
  }

  async exportContacts() {
    const response = await firstValueFrom(this.contactService.exportContacts(this.selectedContactOption));
    if (response) {
      saveAs(new Blob([response], { type: 'application/octet-stream' }), 'contacts.csv');
      this.snackBar.open(
        this.translate.instant('CONTACT_PREFERENCES.CONTACTS_HAVE_BEEN_DOWNLOADED'),
        this.translate.instant('CONTACT_PREFERENCES.OK')
      );
    }
  }

  handleClickOnImportContact() {
    this.dialog.open(ImportContactsComponent);
  }

  protected async onChangeContactMatching() {
    this.contactMatchingLoading = true;
    await firstValueFrom(
      this.configService.updatePreferences({
        calling: {
          ...this.callingConfig,
          disableContactMatching: !this.contactMatching.value,
        },
      })
    );
    this.contactMatchingLoading = false;
  }
}
